// Gráfico de constantes UCI (Fase 4, Tanda 5b, ver PLAN.md "113 — Bitácora
// UCI Adulto") — versión PDF del `GraficoEnfermeria` del cliente
// (packages/hce-chart/.../uci/GraficoEnfermeria.tsx): N series superpuestas
// sobre el mismo turno de 24h (8→7), cada una con su propia escala numérica
// — ver ese archivo para la razón de "escala por serie" y "símbolo por
// punto" (confirmado leyendo la página 220 del PDF fuente). Mismo criterio
// que partograma.ts/denverII.ts: geometría acá, dibujo vectorial con
// pdf-lib.
//
// El papel imprime 4 columnas de números a la izquierda de la grilla (una
// por serie). Acá se resuelve con una leyenda de rango por serie bajo el
// gráfico — mismo criterio que ya usa el componente del cliente y que el
// resto de HCE (equivalencia funcional, no facsímil).
import { rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { BORDE_SUTIL, GRIS_OSCURO, INK, MUTED } from './layout';

export const COLUMNAS_HORARIAS_UCI_PDF = [
  '08', '09', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '00', '01', '02', '03', '04', '05', '06', '07',
] as const;
export type ColumnaHorariaUciPdf = (typeof COLUMNAS_HORARIAS_UCI_PDF)[number];

export type SimboloGraficoConstantesPdf = 'circulo' | 'circuloHueco' | 'triangulo' | 'cruz' | 'cuadrado';

export interface VarianteGraficoConstantesPdf {
  readonly clave: string;
  readonly simbolo: SimboloGraficoConstantesPdf;
}

export interface PuntoGraficoConstantesPdf {
  readonly columna: ColumnaHorariaUciPdf;
  readonly valor: number;
  readonly variante?: string;
}

export interface SerieGraficoConstantesPdf {
  readonly clave: string;
  readonly etiqueta: string;
  readonly simbolo: SimboloGraficoConstantesPdf;
  readonly escalaMin: number;
  readonly escalaMax: number;
  readonly variantes?: readonly VarianteGraficoConstantesPdf[];
  readonly puntos: readonly PuntoGraficoConstantesPdf[];
}

/** Wire format serializable de `EspecificacionPdf` (modo 'msp', campo `diagramaGraficoConstantesUci`) — 113/114/115. */
export interface DatosGraficoConstantesUciPdf {
  readonly series: readonly SerieGraficoConstantesPdf[];
}

const ALTO_GRAFICO = 130;
const ALTO_TITULO = 12;
const ALTO_EJE_X = 12;
const ALTO_LEYENDA_POR_SERIE = 10;

// Misma paleta hce-* ya usada en signosVitalesTimeline.ts/partograma.ts — sin colores nuevos.
const PINE = rgb(0x1f / 255, 0x4d / 255, 0x47 / 255);
const PINE_DEEP = rgb(0x15 / 255, 0x37 / 255, 0x33 / 255);
const CLAY = rgb(0xb5 / 255, 0x56 / 255, 0x3c / 255);
const SAGE = rgb(0x6f / 255, 0x8f / 255, 0x6a / 255);
const PALETA_SERIES = [PINE, CLAY, SAGE, PINE_DEEP];

function xDeColumna(columna: ColumnaHorariaUciPdf, areaX: number, anchoColumna: number): number {
  return areaX + COLUMNAS_HORARIAS_UCI_PDF.indexOf(columna) * anchoColumna + anchoColumna / 2;
}

function yDeValor(valor: number, escalaMin: number, escalaMax: number, areaBottom: number, altoGrafico: number): number {
  if (escalaMax === escalaMin) return areaBottom;
  const v = Math.min(Math.max(valor, escalaMin), escalaMax);
  return areaBottom + ((v - escalaMin) / (escalaMax - escalaMin)) * altoGrafico;
}

function simboloDePunto(serie: SerieGraficoConstantesPdf, punto: PuntoGraficoConstantesPdf): SimboloGraficoConstantesPdf {
  return serie.variantes?.find((v) => v.clave === punto.variante)?.simbolo ?? serie.simbolo;
}

function dibujarSimbolo(pagina: PDFPage, tipo: SimboloGraficoConstantesPdf, cx: number, cy: number, color: ReturnType<typeof rgb>): void {
  switch (tipo) {
    case 'circulo':
      pagina.drawCircle({ x: cx, y: cy, size: 2, color });
      return;
    case 'circuloHueco':
      pagina.drawCircle({ x: cx, y: cy, size: 2, borderColor: color, borderWidth: 1, color: undefined });
      return;
    case 'cuadrado':
      pagina.drawRectangle({ x: cx - 2, y: cy - 2, width: 4, height: 4, color });
      return;
    case 'cruz':
      pagina.drawLine({ start: { x: cx - 2.5, y: cy - 2.5 }, end: { x: cx + 2.5, y: cy + 2.5 }, thickness: 1, color });
      pagina.drawLine({ start: { x: cx - 2.5, y: cy + 2.5 }, end: { x: cx + 2.5, y: cy - 2.5 }, thickness: 1, color });
      return;
    case 'triangulo':
      // drawSvgPath traslada por (x,y) y LUEGO invierte el eje Y (scale(1,-1)) — a
      // diferencia de drawCircle/drawRectangle/drawLine, que usan el eje Y de la
      // página directo. Sin pasar x/y explícitos, pdf-lib usa el cursor interno de
      // la página (this.x/this.y) como traslación por defecto — no (0,0) — así que
      // el triángulo terminaba invisible (fuera de la página). Ancla en (cx,cy) y
      // coordenadas del path ya en el sistema invertido (Y negativo = hacia arriba).
      pagina.drawSvgPath('M 0 -3 L -3 2.5 L 3 2.5 Z', { x: cx, y: cy, color });
      return;
  }
}

/** Espacio vertical a reservar con `cursor.dibujo(altoDibujoGraficoConstantesUci(datos), ...)` — 0 si no hay ninguna serie con puntos. */
export function altoDibujoGraficoConstantesUci(datos: DatosGraficoConstantesUciPdf): number {
  if (datos.series.every((s) => s.puntos.length === 0)) return 0;
  return ALTO_TITULO + ALTO_GRAFICO + ALTO_EJE_X + datos.series.length * ALTO_LEYENDA_POR_SERIE;
}

/** Dibuja el gráfico de constantes (N series, escala propia por serie, mismo turno de 24h) sobre una `PDFPage` cruda, entregada por `CursorPdf.dibujo()`. Pura: no toca I/O, solo `pdf-lib`. */
export function dibujarGraficoConstantesUci(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, datos: DatosGraficoConstantesUciPdf): void {
  if (datos.series.every((s) => s.puntos.length === 0)) return;

  let y = origen.yTop - 10;
  pagina.drawText('Gráfico de constantes', { x: origen.x, y, size: 9, font: fontRegular, color: GRIS_OSCURO });
  y -= ALTO_TITULO;

  const areaTop = y;
  const areaBottom = y - ALTO_GRAFICO;
  const areaX = origen.x;
  const anchoColumna = anchoDisponible / COLUMNAS_HORARIAS_UCI_PDF.length;

  pagina.drawRectangle({ x: areaX, y: areaBottom, width: anchoDisponible, height: ALTO_GRAFICO, borderColor: BORDE_SUTIL, borderWidth: 0.5, color: undefined });
  for (let i = 0; i <= 8; i++) {
    const yy = areaBottom + (i / 8) * ALTO_GRAFICO;
    pagina.drawLine({ start: { x: areaX, y: yy }, end: { x: areaX + anchoDisponible, y: yy }, thickness: 0.35, color: BORDE_SUTIL });
  }
  for (const [i] of COLUMNAS_HORARIAS_UCI_PDF.entries()) {
    const xx = areaX + i * anchoColumna;
    pagina.drawLine({ start: { x: xx, y: areaBottom }, end: { x: xx, y: areaTop }, thickness: 0.35, color: BORDE_SUTIL });
  }

  for (const [i, serie] of datos.series.entries()) {
    const color = PALETA_SERIES[i % PALETA_SERIES.length];
    const puntosOrdenados = [...serie.puntos].sort((a, b) => COLUMNAS_HORARIAS_UCI_PDF.indexOf(a.columna) - COLUMNAS_HORARIAS_UCI_PDF.indexOf(b.columna));
    for (const [j, punto] of puntosOrdenados.entries()) {
      const cx = xDeColumna(punto.columna, areaX, anchoColumna);
      const cy = yDeValor(punto.valor, serie.escalaMin, serie.escalaMax, areaBottom, ALTO_GRAFICO);
      if (j > 0) {
        const anterior = puntosOrdenados[j - 1];
        pagina.drawLine({
          start: { x: xDeColumna(anterior.columna, areaX, anchoColumna), y: yDeValor(anterior.valor, serie.escalaMin, serie.escalaMax, areaBottom, ALTO_GRAFICO) },
          end: { x: cx, y: cy },
          thickness: 1,
          color,
        });
      }
      dibujarSimbolo(pagina, simboloDePunto(serie, punto), cx, cy, color);
    }
  }

  y = areaBottom - ALTO_EJE_X + 9;
  pagina.drawText(COLUMNAS_HORARIAS_UCI_PDF[0], { x: areaX, y, size: 7, font: fontRegular, color: MUTED });
  pagina.drawText(COLUMNAS_HORARIAS_UCI_PDF[COLUMNAS_HORARIAS_UCI_PDF.length - 1], { x: areaX + anchoDisponible - 10, y, size: 7, font: fontRegular, color: MUTED });
  y -= ALTO_EJE_X - 3;

  for (const [i, serie] of datos.series.entries()) {
    const color = PALETA_SERIES[i % PALETA_SERIES.length];
    dibujarSimbolo(pagina, serie.simbolo, areaX + 4, y - 3, color);
    pagina.drawText(`${serie.etiqueta} (${serie.escalaMin}-${serie.escalaMax})`, { x: areaX + 11, y: y - 5, size: 7, font: fontRegular, color: INK });
    y -= ALTO_LEYENDA_POR_SERIE;
  }
}
